import {Direction,EoWriter,PacketFamily,PacketAction} from 'eolib';
import type {Client} from '@/client';
import {GameState} from '@/game-state';
import {HealthBar,NpcAttackAnimation,EffectTargetNpc} from '@/render';
import {EquipmentSlot} from '@/equipment';
import {pvpState} from '@/pvp';
import {registerBookWisdomRequirements,preloadBookWisdomRequirements} from '@/utils/summoning-requirement';
export type Companion={index:number;npc_id:number;owner:number;hp:number;remaining_ms:number;target:number|null;fight_nearby?:boolean;player_target?:number|null;book_instance_id:number;book_xp:number;book_level:number};
export type BookProgress={instance_id:number;item_id:number;npc_id:number;xp:number;level:number;level_xp:number;next_xp:number|null;equipped:boolean;location:string};
export type SummoningState={map_id:number;equipped_book:number;empty_book:number;spell_id:number;books:{item_id:number;npc_id:number}[];book_instances:BookProgress[];summons:Companion[];wisdom:number;learned:boolean;cooldown_ms:number;cooldown_until:number;message?:string|null};
type Store={snapshot?:SummoningState;listeners:Set<()=>void>;equipmentTimer?:ReturnType<typeof setTimeout>};
const stores=new WeakMap<Client,Store>();
function equippedSummoningBook(c:Client,s?:SummoningState){
 const item=c.inventoryController.getEquipmentArray()[EquipmentSlot.Weapon];
 const name=c.getEifRecordById(item)?.name;
 return item>0&&(item===s?.empty_book||s?.books.some(b=>b.item_id===item)||name==='Empty Book'||name?.startsWith('Book of '))?item:0;
}
function store(c:Client){let s=stores.get(c);if(!s){s={listeners:new Set()};stores.set(c,s);
 c.inventoryController.subscribeEquipmentChanged(()=>{
  const state=stores.get(c);if(!state)return;
  const snapshot=state.snapshot?.map_id===c.mapId?state.snapshot:undefined;
  const equipped=equippedSummoningBook(c,snapshot);
  if(snapshot)state.snapshot={...snapshot,equipped_book:equipped};
  // Equipment arrives separately from the extension snapshot. Always notify,
  // including first login/map entry; the slot must not depend on packet order.
  for(const fn of state.listeners)fn();
  clearTimeout(state.equipmentTimer);
  state.equipmentTimer=setTimeout(()=>{state.equipmentTimer=undefined;commandCompanion(c,'summon_status');},250);
 });
 pvpState(c).subscribe(()=>{if(!pvpState(c).forced()&&playerCommands.has(c)){
  playerCommands.delete(c);commandCompanion(c,'summon_follow');
 }});
 }return s;}
export function getSummoning(c:Client){
 const s=store(c).snapshot;if(s?.map_id!==c.mapId)return undefined;
 const equipped=equippedSummoningBook(c,s);
 return {...s,equipped_book:equipped};
}
export function subscribeSummoning(c:Client,fn:()=>void){const s=store(c);s.listeners.add(fn);return ()=>{s.listeners.delete(fn);};}
export function clearSummoning(c:Client){playerCommands.delete(c);const s=store(c);clearTimeout(s.equipmentTimer);s.equipmentTimer=undefined;s.snapshot=undefined;for(const fn of s.listeners)fn();}
export function ownCompanion(c:Client){return getSummoning(c)?.summons.find(s=>s.owner===c.playerId);}
/** Slot one is a temporary summon shortcut; never overwrite the saved hotbar. */
export function bookSummonSlot(c:Client,index:number){
 if(index!==0)return null;
 const s=getSummoning(c);
 // Show the shortcut whenever a book is equipped, even before its snapshot or
 // the learned-spell list arrives. Native summoning still enforces all rules.
 return equippedSummoningBook(c,s)>0?s?.spell_id??48:null;
}
export function isCompanion(c:Client,index:number){return !!getSummoning(c)?.summons.some(s=>s.index===index);}
export function summonCooldown(c:Client){return Math.max(0,(getSummoning(c)?.cooldown_until??0)-Date.now());}
export function summonFromButton(c:Client){
 const s=getSummoning(c);
 if(!equippedSummoningBook(c,s)||ownCompanion(c)||summonCooldown(c)>0)return false;
 return commandCompanion(c,'summon');
}
export function commandCompanion(c:Client,action:'summon'|'summon_dismiss'|'summon_target'|'summon_status'|'summon_assist'|'summon_follow'|'summon_player',index?:number){
 if(c.state!==GameState.InGame||c.worldLoading)return false;
 if(action==='summon_player'&&!pvpState(c).forced())return false;
 if(action!=='summon_player'&&action!=='summon_status')playerCommands.delete(c);
 const w=new EoWriter();w.addString(JSON.stringify({action,node:index===undefined?'':String(index)}));
 try{c.bus.sendBuf(58 as PacketFamily,PacketAction.Request,w.toByteArray());return true;}catch{return false;}
}
const playerCommands=new WeakMap<Client,{target:number;next:number}>();
export function commandCompanionPlayer(c:Client,id:number){
 if(id===c.playerId||!ownCompanion(c)||!commandCompanion(c,'summon_player',id))return false;
 playerCommands.set(c,{target:id,next:Date.now()+1000});return true;
}
export function tickCompanionPlayer(c:Client){
 const intent=playerCommands.get(c);if(!intent)return;
 const active=ownCompanion(c);
 if(!pvpState(c).forced()||!active||!c.getCharacterById(intent.target)||c.state!==GameState.InGame||c.worldLoading){
  playerCommands.delete(c);if(active)commandCompanion(c,'summon_follow');return;
 }
 if(Date.now()>=intent.next){
  // A different command, death or server rejection must not restart a cancelled target.
  if(active.player_target!==intent.target){playerCommands.delete(c);return;}
  intent.next=Date.now()+1000;commandCompanion(c,'summon_player',intent.target);
 }
}
const int=(v:unknown,min:number,max:number)=>typeof v==='number'&&Number.isSafeInteger(v)&&v>=min&&v<=max;
export function receiveSummoning(c:Client,data:unknown){
 if(!data||typeof data!=='object')return;
 const d=data as Record<string,unknown>;if(d.map_id!==c.mapId)return;
 if(d.kind==='summon_capture'){
  if(!int(d.old_item,1,64008)||!int(d.new_item,1,64008))return;
  const s=getSummoning(c),equipment=c.inventoryController.getEquipmentArray();
  if((s&&s.empty_book!==d.old_item)||c.getEifRecordById(d.old_item as number)?.name!=='Empty Book'||equipment[EquipmentSlot.Weapon]!==d.old_item||!c.getEifRecordById(d.new_item as number)?.name.startsWith('Book of '))return;
  c.inventoryController.setEquipmentSlot(EquipmentSlot.Weapon,0);
  // Native PaperdollAgree immediately supplies the new item and its stats.
  c.inventoryController.refreshEnhancementDisplay();return;
 }
 if(d.kind==='summon_remove'){
  if(!int(d.index,0,249))return;const index=d.index as number;
  c.nearby.npcs=c.nearby.npcs.filter(n=>n.index!==index);
  c.animationController.npcAnimations.delete(index);c.animationController.pendingNpcAnimations.delete(index);c.animationController.npcHealthBars.delete(index);
  return;
 }
 if(d.kind==='summon_heal'){
  if(!int(d.index,0,249)||!int(d.amount,0,2147483647)||!int(d.hp,0,100)||!int(d.spell_id,1,64008)||!c.getNpcByIndex(d.index as number))return;
  if((d.amount as number)>0)c.animationController.npcHealthBars.set(d.index as number,new HealthBar(d.hp as number,0,d.amount as number));
  c.spellController.playSpellEffect(d.spell_id as number,new EffectTargetNpc(d.index as number));return;
 }
 if(d.kind==='summon_hit'){
  if(!int(d.attacker,0,249)||!int(d.target,0,249)||!int(d.damage,0,2147483647)||!int(d.hp,0,100))return;
  const a=c.getNpcByIndex(d.attacker as number),b=c.getNpcByIndex(d.target as number);if(!a||!b)return;
  const dx=b.coords.x-a.coords.x,dy=b.coords.y-a.coords.y;
  a.direction=Math.abs(dx)>Math.abs(dy)?dx>0?Direction.Right:Direction.Left:dy>0?Direction.Down:Direction.Up;
  c.animationController.pendingNpcAnimations.set(a.index,new NpcAttackAnimation());
  c.animationController.npcHealthBars.set(b.index,new HealthBar(d.hp as number,d.damage as number));
  // Companion combat never changes player HP or player health bars.
  return;
 }
 if(d.kind==='summon_player_attack'){
  if(!int(d.attacker,0,249)||!int(d.target,1,64008))return;
  const a=c.getNpcByIndex(d.attacker as number),b=c.getCharacterById(d.target as number);if(!a||!b)return;
  const dx=b.coords.x-a.coords.x,dy=b.coords.y-a.coords.y;
  a.direction=Math.abs(dx)>Math.abs(dy)?dx>0?Direction.Right:Direction.Left:dy>0?Direction.Down:Direction.Up;
  c.animationController.pendingNpcAnimations.set(a.index,new NpcAttackAnimation());return;
 }
 if(d.kind!=='summoning'||!int(d.equipped_book,0,64008)||!int(d.empty_book,1,64008)||!int(d.spell_id,1,64008)||typeof d.learned!=='boolean'||!Array.isArray(d.books)||!Array.isArray(d.summons)||d.summons.length>250||d.books.length>1000)return;
 if(!d.summons.every(s=>s&&int(s.index,0,249)&&int(s.npc_id,1,64008)&&int(s.owner,0,64008)&&int(s.hp,0,100)&&int(s.remaining_ms,0,180000)&&(s.target===null||int(s.target,0,249))))return;
 if(!d.summons.every(s=>(s.fight_nearby===undefined||typeof s.fight_nearby==='boolean')&&(s.player_target===undefined||s.player_target===null||int(s.player_target,1,64008))))return;
 if(!d.books.every(b=>b&&int(b.item_id,1,64008)&&int(b.npc_id,1,64008)))return;
 if(!Array.isArray(d.book_instances)||d.book_instances.length>10000||!d.book_instances.every(b=>b&&int(b.instance_id,1,2147483647)&&int(b.item_id,1,64008)&&int(b.npc_id,1,64008)&&int(b.xp,0,2147483647)&&int(b.level,1,100)&&int(b.level_xp,0,2147483647)&&(b.next_xp===null||int(b.next_xp,0,2147483647))&&typeof b.equipped==='boolean'&&typeof b.location==='string'))return;
 if(!int(d.cooldown_ms,0,40000))return;
 registerBookWisdomRequirements(c,d.books as {item_id:number;npc_id:number}[]);
 preloadBookWisdomRequirements(c).then(changed=>{if(changed)c.inventoryController.refreshEnhancementDisplay();}).catch(()=>{});
 const s=store(c);s.snapshot={...d,cooldown_until:Date.now()+(d.cooldown_ms as number)} as unknown as SummoningState;
 if(typeof d.message==='string'&&d.message)c.toastController.show(d.message);
 for(const fn of s.listeners)fn();
}
